import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { LogOut, Shield, Crown, Sparkles, Check, Mail } from "lucide-react";
import ReferralCard from "@/components/ReferralCard";
import ReferralLeaderboard from "@/components/ReferralLeaderboard";
import OwnerInvites from "@/components/OwnerInvites";
import NudgePreviewButton from "@/components/NudgePreviewButton";
import HouseholdCard from "@/components/HouseholdCard";

const PREMIUM_BULLETS = [
  "Unlimited Find Me Money analyses",
  "AI Money Coach chat",
  "Subscription detector & cancel reminders",
  "Monthly Money Report with 3 priorities",
  "Savings goals with progress tracking",
  "Spending trends & advanced insights",
  "Personalized savings plan",
  "Unlimited transaction history",
];

export default function Profile() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [plan, setPlan] = useState("moneywise_premium_yearly");
  const [busy, setBusy] = useState(false);
  const [prefs, setPrefs] = useState({ weekly_nudge: true, bill_alerts: true, streak_emails: true });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get("/email_prefs"); setPrefs({ ...prefs, ...data }); } catch {}
    })();
    // eslint-disable-next-line
  }, []);

  const togglePref = async (key) => {
    const next = { ...prefs, [key]: !prefs[key] };
    setPrefs(next);
    try { await api.patch("/email_prefs", { [key]: next[key] }); }
    catch { setPrefs(prefs); toast.error("Could not save"); }
  };

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post("/payments/cancel_subscription");
      await refresh();
      toast.info("Subscription cancelled. You're back on Free.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not cancel");
    }
    setBusy(false);
  };

  return (
    <div className="px-6 pt-10 pb-6">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-slate-900 text-white flex items-center justify-center font-manrope font-bold text-xl">{user.name.slice(0, 1).toUpperCase()}</div>
        <div>
          <div className="font-manrope text-xl font-extrabold tracking-tight text-slate-900" data-testid="profile-name">{user.name}</div>
          <div className="text-slate-500 text-sm">{user.email}</div>
        </div>
      </div>

      {!user.premium ? (
        <div className="mt-8 rounded-3xl bg-slate-900 text-white p-6 relative overflow-hidden">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-emerald-500/20 blur-3xl" />
          <div className="relative">
            <div className="flex items-center gap-2 text-emerald-300 text-xs uppercase tracking-widest font-semibold"><Crown size={14} /> Premium</div>
            <div className="mt-2 font-manrope text-2xl font-extrabold tracking-tight">MoneyWise found money in your data.</div>
            <p className="mt-2 text-slate-300 text-sm">Premium helps you track and act on those opportunities every month.</p>

            {/* Plan picker */}
            <div className="mt-6 space-y-2">
              <PlanCard
                selected={plan === "moneywise_premium_yearly"}
                onClick={() => setPlan("moneywise_premium_yearly")}
                title="Yearly"
                price="$39.99"
                cadence="/year"
                badge="Save 33%"
                sub="$3.33/month billed annually"
                testId="plan-yearly"
              />
              <PlanCard
                selected={plan === "moneywise_premium_monthly"}
                onClick={() => setPlan("moneywise_premium_monthly")}
                title="Monthly"
                price="$4.99"
                cadence="/month"
                sub="Cancel anytime"
                testId="plan-monthly"
              />
            </div>

            <ul className="mt-6 space-y-1.5">
              {PREMIUM_BULLETS.map((b, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-slate-200"><Check size={14} className="text-emerald-400" /> {b}</li>
              ))}
            </ul>

            <button
              data-testid="upgrade-btn"
              onClick={upgrade}
              disabled={busy}
              className="mt-6 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-full py-4 font-semibold active:scale-[0.98] transition-transform duration-150 disabled:opacity-60"
            >
              {busy ? "Redirecting to Stripe…" : "Upgrade with Stripe"}
            </button>
            <p className="mt-3 text-[11px] text-slate-400 text-center">Secure checkout by Stripe. Test card: 4242 4242 4242 4242</p>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl border border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-2 text-emerald-700 text-xs uppercase tracking-widest font-semibold"><Sparkles size={14} /> Premium active</div>
          <div className="mt-2 font-manrope text-xl font-bold text-slate-900">You have full access.</div>
          <button
            data-testid="cancel-sub-btn"
            onClick={cancel}
            disabled={busy}
            className="mt-4 text-sm text-slate-600 hover:text-slate-900 underline underline-offset-2 disabled:opacity-60"
          >
            {busy ? "…" : "Cancel subscription"}
          </button>
        </div>
      )}

      <div className="mt-8 rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2 mb-3"><Mail size={16} className="text-slate-400" strokeWidth={1.75} /><div className="text-sm font-semibold text-slate-900">Email preferences</div></div>
        <div className="space-y-2">
          <PrefRow label="Weekly recap (Sundays)" hint="Your week in numbers + one tip" on={!!prefs.weekly_nudge} onToggle={() => togglePref("weekly_nudge")} testId="pref-weekly" />
          <PrefRow label="Bill alerts" hint="Heads up 3 days before big recurring charges" on={!!prefs.bill_alerts} onToggle={() => togglePref("bill_alerts")} testId="pref-bill" />
          <PrefRow label="Streak celebrations" hint="Congrats when you hit a cancel milestone" on={!!prefs.streak_emails} onToggle={() => togglePref("streak_emails")} testId="pref-streak" />
        </div>
        <NudgePreviewButton />
      </div>

      <div className="mt-4">
        <ReferralCard />
        <ReferralLeaderboard />
      </div>
      <HouseholdCard />
      <OwnerInvites />

      <div className="mt-4 rounded-2xl border border-slate-200 p-5">
        <div className="flex items-center gap-2"><Shield size={16} className="text-slate-400" strokeWidth={1.75} /><div className="text-sm font-semibold text-slate-900">Your data is private</div></div>
        <p className="mt-2 text-xs text-slate-500 leading-relaxed">MoneyWise never stores your bank passwords. Transaction data is tied to your account only. This app provides educational insights, not financial advice.</p>
      </div>

      <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="mt-8 w-full bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-full py-3 font-semibold flex items-center justify-center gap-2 transition-colors">
        <LogOut size={16} /> Log out
      </button>
    </div>
  );
}

function PlanCard({ selected, onClick, title, price, cadence, sub, badge, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full text-left rounded-2xl border p-4 transition-colors duration-200 ${selected ? "bg-white/10 border-emerald-400" : "bg-white/5 border-white/10 hover:border-white/30"}`}
    >
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-2">
          <div className={`w-4 h-4 rounded-full border-2 ${selected ? "border-emerald-400 bg-emerald-400" : "border-white/30"}`} />
          <span className="font-manrope font-bold text-white">{title}</span>
          {badge && <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500 text-slate-900 rounded-full px-2 py-0.5">{badge}</span>}
        </div>
        <div className="font-manrope font-extrabold tracking-tight text-white tabular-nums">{price}<span className="text-slate-400 text-xs font-semibold">{cadence}</span></div>
      </div>
      <div className="mt-1 text-xs text-slate-400 pl-6">{sub}</div>
    </button>
  );
}

function PrefRow({ label, hint, on, onToggle, testId }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1">
      <div className="min-w-0">
        <div className="text-sm font-medium text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-500">{hint}</div>
      </div>
      <button
        data-testid={testId}
        onClick={onToggle}
        role="switch"
        aria-checked={on}
        className={`shrink-0 w-11 h-6 rounded-full relative transition-colors duration-200 ${on ? "bg-emerald-500" : "bg-slate-300"}`}
      >
        <span className={`absolute top-0.5 ${on ? "left-5" : "left-0.5"} w-5 h-5 rounded-full bg-white shadow transition-all duration-200`} />
      </button>
    </div>
  );
}
